import { Link } from 'react-router-dom'
import { useState } from 'react'
import {
  useDismissSetupItem,
  useRestoreSetupItem,
  useSetupChecklist,
} from '@/modules/setup-checklist/hooks'
import { ChromeIcon } from '../ChromeIcon'
import s from './SetupChecklistWidget.module.css'

/**
 * The sidebar setup checklist that replaces `/setup`.
 *
 * The wizard it replaces was modal and linear: one order, all of it, or nothing.
 * An owner who hit "Save & continue later" was never shown it again, which is
 * how accounts stayed half-configured forever. So the two things that matter
 * here are that it is **persistent** (it is in the rail on every page, not a
 * page you have to remember to go back to) and **non-linear** (four independent
 * rows, each with its own destination).
 *
 * It is also the *only* place setup lives now. the second half keeps these
 * items off Home's needs-you feed — Home is for what needs the owner today, and
 * a task that has sat there since signup teaches them the list is partly noise.
 *
 * Disappears entirely once every row is done or dismissed. A finished checklist
 * is clutter, and the owner can always see the underlying state in Settings.
 */
export function SetupChecklistWidget({ enabled, onNavigate }: { enabled: boolean; onNavigate?: () => void }) {
  const { data } = useSetupChecklist(enabled)
  const dismiss = useDismissSetupItem()
  const restore = useRestoreSetupItem()
  // Collapsed by default once the owner has made a dent — the rail is not the
  // place for a permanently expanded four-item list.
  const [open, setOpen] = useState(true)

  if (!data || data.complete) return null

  const dismissed = data.items.filter((i) => i.status === 'DISMISSED')
  const active = data.items.filter((i) => i.status !== 'DISMISSED')
  const done = active.filter((i) => i.status === 'DONE').length

  return (
    <div className={s.widget}>
      <button
        type="button"
        className={s.header}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={s.headerName}>Finish setting up</span>
        <span className={s.headerCount}>
          {done}/{active.length}
        </span>
        {/* One asset, rotated — the set has no chevron-up, and inventing a
            second glyph for the same affordance is how icon sets drift. */}
        <ChromeIcon
          name="chevronDown"
          size={14}
          className={[s.headerChevron, open ? s.headerChevronOpen : ''].join(' ')}
        />
      </button>

      {open && (
        <ul className={s.list}>
          {active.map((item) => {
            const isDone = item.status === 'DONE'
            return (
              <li key={item.key} className={[s.row, isDone ? s.rowDone : ''].join(' ')}>
                {/* The tick is drawn rather than an asset: this is a checkbox
                    state, not one of the chrome destination marks. */}
                <span className={[s.check, isDone ? s.checkDone : ''].join(' ')} aria-hidden="true">
                  {isDone ? (
                    <svg viewBox="0 0 12 12" width="11" height="11" fill="none">
                      <path
                        d="M2.5 6.2l2.3 2.3 4.7-5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                </span>

                {isDone ? (
                  // A finished row is a receipt, not a link — there is nothing
                  // left to do on it, and making it clickable invites the owner
                  // to re-enter a flow they already completed.
                  <span className={s.title}>{item.title}</span>
                ) : (
                  <Link to={item.href} className={s.title} onClick={onNavigate} title={item.detail}>
                    {item.title}
                  </Link>
                )}

                {!isDone && item.dismissible && (
                  <button
                    type="button"
                    className={s.skip}
                    onClick={() => dismiss.mutate(item.key)}
                    disabled={dismiss.isPending}
                    aria-label={`Skip ${item.title}`}
                    title="Skip this — you can bring it back below"
                  >
                    <ChromeIcon name="close" size={12} />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* Skipped items stay recoverable. A dismissal the owner cannot undo is a
          trap, and the count is the only hint they left anything behind. */}
      {open && dismissed.length > 0 && (
        <div className={s.skipped}>
          <span className={s.skippedLabel}>
            {dismissed.length} skipped
          </span>
          <button
            type="button"
            className={s.skippedRestore}
            onClick={() => dismissed.forEach((i) => restore.mutate(i.key))}
            disabled={restore.isPending}
          >
            Show
          </button>
        </div>
      )}
    </div>
  )
}
